import { ActionReducerMapBuilder, createSlice } from "@reduxjs/toolkit";
import { CurrentUser, SliceStatus } from "interfaces";

import {
  anonymousLogin,
  checkAuthState,
  login,
  logout,
  register,
  setUser,
  setUserCreatedInSession,
  thirdPartyAuth
} from "./userActions";

type userSliceState = {
  current: CurrentUser;
  status: SliceStatus;
  thirdPartyAuthStatus: SliceStatus;
  error: string;
  userCreatedInSession: boolean;
};

const initialState = {
  current: null as CurrentUser,
  status: SliceStatus.Idle,
  thirdPartyAuthStatus: SliceStatus.Idle,
  error: "",
  userCreatedInSession: false
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<userSliceState>) =>
    builder
      .addCase(register.pending, state => ({
        ...state,
        status: SliceStatus.Pending
      }))
      .addCase(register.rejected, state => ({
        ...state,
        status: SliceStatus.Rejected
      }))
      .addCase(register.fulfilled, state => ({
        ...state,
        status: SliceStatus.Resolved
      }))
      .addCase(login.pending, state => ({
        ...state,
        status: SliceStatus.Pending
      }))
      .addCase(login.rejected, state => ({
        ...state,
        status: SliceStatus.Rejected
      }))
      .addCase(login.fulfilled, state => ({
        ...state,
        status: SliceStatus.Resolved
      }))
      .addCase(thirdPartyAuth.pending, state => ({
        ...state,
        thirdPartyAuthStatus: SliceStatus.Pending
      }))
      .addCase(thirdPartyAuth.rejected, state => ({
        ...state,
        thirdPartyAuthStatus: SliceStatus.Rejected
      }))
      .addCase(thirdPartyAuth.fulfilled, state => ({
        ...state,
        thirdPartyAuthStatus: SliceStatus.Resolved
      }))

      .addCase(checkAuthState.fulfilled, (state, { payload }) => ({
        ...state,
        current: payload as CurrentUser
      }))

      .addCase(setUser, (state, action) => ({
        ...state,
        current: action.payload
      }))
      .addCase(setUserCreatedInSession, (state, action) => ({
        ...state,
        userCreatedInSession: action.payload
      }))
      .addDefaultCase(state => state)
});

export const { reducer: userReducer, name: userReducerName } = userSlice;

export type TUserActions =
  | ReturnType<typeof register>
  | ReturnType<typeof login>
  | ReturnType<typeof thirdPartyAuth>
  | ReturnType<typeof logout>
  | ReturnType<typeof setUser>
  | ReturnType<typeof setUserCreatedInSession>;

export const userActions = {
  login,
  register,
  thirdPartyAuth,
  logout,
  setUser,
  checkAuthState,
  anonymousLogin,
  setUserCreatedInSession
};
export type UserState = ReturnType<typeof userReducer>;
